import React, {useMemo} from 'react';
import {Link as RouterLink} from 'react-router';
import {
    Box,
    Chip,
    Divider,
    Link as MuiLink,
    Stack,
    Typography,
} from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import LanguageIcon from '@mui/icons-material/Language';
import TabLoading from './TabLoading';
import TabEmpty from './TabEmpty';
import {highlightEntityInSnippet} from '../utils/highlightEntityInSnippet';
import {usePagination} from './usePagination';
import TabPagination from './TabPagination';

// ─── helpers ──────────────────────────────────────────────────────────────────

const SOURCE_TYPE_META = {
    PDF:   {label: 'PDF',    icon: PictureAsPdfIcon, color: 'error'},
    AUDIO: {label: 'Audio',  icon: AudioFileIcon,    color: 'secondary'},
    VIDEO: {label: 'Video',  icon: VideoFileIcon,    color: 'info'},
    OSINT: {label: 'Web',    icon: LanguageIcon,     color: 'primary'},
};

function getSourceMeta(type) {
    return SOURCE_TYPE_META[type] || {label: type || 'Document', icon: ArticleIcon, color: 'default'};
}

// ─── sub-components ───────────────────────────────────────────────────────────

const ReportChip = ({report}) => {
    const label = report.reportTitle || report.reportId?.slice(0, 8) || 'Report';
    const isFintel = report.reportStatus === 'approved';
    return (
        <Chip
            component={RouterLink}
            to={`/products/${report.reportId}/view`}
            label={label}
            size="small"
            color={isFintel ? 'success' : 'default'}
            variant="outlined"
            clickable
            sx={{maxWidth: 220, fontSize: 11}}
        />
    );
};

const MentionRow = ({mention, entityHighlightContext}) => {
    const {label: typeLabel, icon: TypeIcon, color} = getSourceMeta(mention.docSourceType);
    const title = mention.docTitle || mention.docSource || 'Untitled document';
    // Prefer the stored article URL; fall back to docSource if it is already a full URL
    const articleUrl = mention.docUrl ||
        (mention.docSource?.startsWith('http') ? mention.docSource : null);
    const snippets = mention.snippets || [];

    return (
        <Box sx={{px: 2, py: 1.5}}>
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <TypeIcon sx={{mt: 0.3, color: `${color}.main`, flexShrink: 0}}/>

                <Stack flex={1} spacing={0.75} minWidth={0}>
                    {/* Title / link */}
                    {articleUrl ? (
                        <MuiLink
                            href={articleUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            fontWeight={500}
                            noWrap
                            sx={{display: 'block'}}
                        >
                            {title}
                        </MuiLink>
                    ) : (
                        <Typography fontWeight={500} noWrap>{title}</Typography>
                    )}

                    {/* Meta row */}
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Chip
                            label={typeLabel}
                            size="small"
                            color={color}
                            variant="filled"
                            sx={{fontSize: 11, height: 20}}
                        />
                        <Typography variant="caption" color="text.secondary">
                            {mention.mentionCount} mention{mention.mentionCount !== 1 ? 's' : ''}
                        </Typography>

                        {/* Linked reports */}
                        {mention.reports?.length > 0 && (
                            <>
                                <Typography variant="caption" color="text.disabled">·</Typography>
                                <Typography variant="caption" color="text.secondary">used in:</Typography>
                                {mention.reports.map(r => (
                                    <ReportChip key={r.reportId} report={r}/>
                                ))}
                            </>
                        )}
                    </Stack>

                    {/* Text snippets */}
                    {snippets.length > 0 && (
                        <Stack spacing={0.5}>
                            {snippets.map((snippet, i) => (
                                <Box
                                    key={i}
                                    sx={{
                                        borderLeft: 3,
                                        borderColor: `${color}.light`,
                                        pl: 1.5,
                                        py: 0.25,
                                        bgcolor: 'action.hover',
                                        borderRadius: '0 4px 4px 0',
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        component="div"
                                        sx={{
                                            fontStyle: 'italic',
                                            lineHeight: 1.5,
                                            display: '-webkit-box',
                                            WebkitLineClamp: 3,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        &ldquo;{highlightEntityInSnippet(snippet, entityHighlightContext)}&rdquo;
                                    </Typography>
                                </Box>
                            ))}
                        </Stack>
                    )}
                </Stack>
            </Stack>
        </Box>
    );
};

// ─── main component ───────────────────────────────────────────────────────────

const MentionsTab = ({mentions, loading, entityHighlightContext}) => {
    const {page, setPage, pageItems, totalPages, hasPagination} = usePagination(mentions);

    // Group the current page slice by source type for section headers.
    const grouped = useMemo(() => {
        const order = ['FINTEL', 'PDF', 'OSINT', 'AUDIO', 'VIDEO'];
        const map = {};
        for (const m of pageItems) {
            const key = m.docSourceType || 'OTHER';
            if (!map[key]) map[key] = [];
            map[key].push(m);
        }
        const keys = Object.keys(map).sort((a, b) => {
            const ai = order.indexOf(a);
            const bi = order.indexOf(b);
            if (ai === -1 && bi === -1) return a.localeCompare(b);
            if (ai === -1) return 1;
            if (bi === -1) return -1;
            return ai - bi;
        });
        return keys.map(key => ({type: key, items: map[key]}));
    }, [pageItems]);

    if (loading) return <TabLoading/>;
    if (!mentions.length) return <TabEmpty message="No source documents found for this entity."/>;

    return (
        <Stack>
        <Stack divider={<Divider/>}>
            {grouped.map(({type, items}) => {
                const {label} = getSourceMeta(type);
                return (
                    <Box key={type}>
                        <Typography
                            variant="overline"
                            color="text.secondary"
                            sx={{px: 2, pt: 1.5, pb: 0.5, display: 'block', fontSize: 11}}
                        >
                            {label} · {items.length} document{items.length !== 1 ? 's' : ''}
                        </Typography>
                        <Stack divider={<Divider sx={{mx: 2}}/>}>
                            {items.map(m => (
                                <MentionRow
                                    key={m.docId}
                                    mention={m}
                                    entityHighlightContext={entityHighlightContext}
                                />
                            ))}
                        </Stack>
                    </Box>
                );
            })}
        </Stack>
        <TabPagination
            hasPagination={hasPagination}
            page={page}
            totalPages={totalPages}
            totalItems={mentions.length}
            onChange={setPage}
        />
        </Stack>
    );
};

export default MentionsTab;
